// DS: CO5.1.1
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { BiData } from '@wix/da-bi/pkg/events';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { MAX_TITLE_LENGTH } from '../../../utils/isValidFolderName';
import { useTranslation } from 'react-i18next';
import withCurrentMediaSource from '../../_hocs/withCurrentMediaSource';
import withCurrentGallection from '../../_hocs/withCurrentGallection';
import withGallections from '../../_hocs/withGallections';
import withCurrentGallectionSubfolders from '../../_hocs/withCurrentGallectionSubfolders';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { createGallection } from '../../actions';
import PremiumForm from '../../../forms/PremiumForm';
import { PremiumFolderType } from '../../../types/gallection';
import yupResolver from '../../../utils/yupResolver';
import { dollarsToPoints } from '../../../utils/points';
import { MediaSourceType } from '../../types';
import { getCoreLevel, getCurrentUserHasCore } from '../../../publicSession/selectors';
import useValidation from './useValidation';
import DisclosureBanner from '@wix/da-ds/pkg/DisclosureBanner';
import BuyCoreBrandedButton from '../../../Button/core/dsPresets/BuyCoreBrandedButton/BuyCoreBrandedButton';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import withGruser from '../../_hocs/withGruser';
import { isGroup as getIsGroup } from '../../../User/helpers';
import newGallectionPopupStyles from './NewGallectionPopup.scss';
export const NewGallectionPopup = ({ currentMediaSource, currentFolderId, gruser, subfolders, gallections: topLevelGallections, coreLevel, hasCoreMembership, onCancel, onDone, createGallection: createGallectionAction })=>{
    const { t } = useTranslation();
    const isGallery = currentMediaSource === MediaSourceType.GALLERY;
    const isPrivateCollection = currentMediaSource === MediaSourceType.PRIVATE_COLLECTION;
    const existingFolderNames = (currentFolderId ? subfolders : topLevelGallections).map((gallection)=>gallection.name);
    const validationSchema = useValidation(coreLevel, isGallery, existingFolderNames);
    const formContext = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationSchema, {
            abortEarly: false
        }),
        defaultValues: {
            title: '',
            price: '',
            folderType: PremiumFolderType.Regular
        }
    });
    const { register, formState, control, errors, getValues, handleSubmit } = formContext;
    useEffect(()=>{
        register('folderType');
    }, [
        register
    ]);
    const isGroup = gruser && getIsGroup(gruser);
    const withPremiumForm = !isGroup && currentMediaSource === MediaSourceType.GALLERY && !currentFolderId; /* PF can not be a sub-folder */ 
    const onSubmit = (data)=>{
        createGallectionAction({
            name: data.title,
            priceInPoints: dollarsToPoints(data.price),
            folderType: data.folderType
        });
        onDone && onDone();
    };
    const onTitleKeyUp = (ev)=>{
        if (formState.isValid && ev.key === 'Enter') {
            handleSubmit(onSubmit);
        }
    };
    const newFolderKeyPart = currentFolderId ? 'newSubGallery' : 'new';
    const heading = t(`mediapopup.gallections.${currentMediaSource}.prompt.${newFolderKeyPart}.title`);
    let subheading = '';
    if (isPrivateCollection) {
        subheading = t('addToCollectionPopover.private.subtitle');
    }
    const folderType = getValues('folderType');
    const isWatcherOnlyFolder = folderType === PremiumFolderType.Watchers;
    const isPaidFolder = folderType === PremiumFolderType.Paid;
    const showBuyCoreCTAasPrimaryButton = !hasCoreMembership && (isPrivateCollection || isWatcherOnlyFolder || isPaidFolder);
    return /*#__PURE__*/ React.createElement(DSModal, {
        isOpen: true,
        onRequestClose: onCancel,
        className: newGallectionPopupStyles['root'],
        contentClassName: newGallectionPopupStyles['content'],
        heading: heading,
        subheading: subheading,
        showHeaderBorder: true,
        showFooterBorder: true,
        footerRight: [
            /*#__PURE__*/ React.createElement(TextButton, {
                key: "cancel",
                onClick: onCancel,
                size: "large",
                variant: "main"
            }, t('popup.cancel')),
            showBuyCoreCTAasPrimaryButton ? /*#__PURE__*/ React.createElement(BuyCoreBrandedButton, {
                size: "large",
                withSaleSparkles: true,
                biData: BiData({
                    evid: 375,
                    referral_info: isWatcherOnlyFolder ? ReferralType.PAYWALL_WATCHERS_ONLY : ReferralType.PAYWALL_PG
                })
            }, t('common.upgradeToCoreCTA')) : /*#__PURE__*/ React.createElement(BrandedButton, {
                key: "submit",
                size: "large",
                variant: "brand-green",
                disabled: formState.isSubmitting,
                onClick: handleSubmit(onSubmit)
            }, t('mediapopup.create'))
        ]
    }, /*#__PURE__*/ React.createElement("div", null, isPrivateCollection && !hasCoreMembership && /*#__PURE__*/ React.createElement(DisclosureBanner, {
        className: newGallectionPopupStyles['non-core-banner-for-private-collections'],
        message: t('mediapopup.newFolderPopup.gallery.nonCoreBanner.message'),
        ctaLabel: t('common.upgradeCoreCTA'),
        ctaBiData: BiData({
            evid: 375,
            referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP
        })
    }), /*#__PURE__*/ React.createElement(Controller, {
        name: "title",
        control: control,
        render: ({ onChange, value, name })=>{
            const error = errors[name];
            return /*#__PURE__*/ React.createElement(TextInput, {
                id: "collection-title-input",
                name: name,
                value: value,
                label: t('mediapopup.gallections.input.name'),
                validationResult: error ? 'error' : undefined,
                details: error === null || error === void 0 ? void 0 : error.message,
                maxLength: MAX_TITLE_LENGTH,
                onChange: onChange,
                onKeyUp: onTitleKeyUp,
                autoFocus: true
            });
        }
    }), withPremiumForm && /*#__PURE__*/ React.createElement(FormProvider, formContext, /*#__PURE__*/ React.createElement(PremiumForm, null))));
};
NewGallectionPopup.displayName = 'NewGallectionPopup';
const enhance = compose(withCurrentMediaSource, withCurrentGallection, withGruser, withGallections, withCurrentGallectionSubfolders, connectMediaStore((state)=>({
        coreLevel: getCoreLevel(state),
        hasCoreMembership: getCurrentUserHasCore(state)
    }), {
    createGallection
}));
export default enhance(NewGallectionPopup);

//# sourceMappingURL=NewGallectionPopup.js.map